import Close from '@components/icons/Close/Close'
import Button from '@components/ui/ButtonV2/Button'
import Checkbox from '@components/ui/inputs/Checkbox'
import FormControl from '@components/ui/inputs/FormControl'
import TextInput from '@components/ui/inputs/TextInput'
import nms from '@components/ui/NewModal/NewModal.module.scss'
import { useDisclosure } from '@lib/helpers/useDisclosure'
import clsx from 'clsx'
import { FC } from 'react'
import { Dialog, Modal, ModalOverlay } from 'react-aria-components'
import { ControllerRenderProps } from 'react-hook-form'
import s from './M61ProfileView.module.scss'

export type PreferenceInputType = {
  inputName: string
  inputType: 'text' | 'date' | 'checkbox' | 'interests'
  checkboxText?: {
    true: string
    false: string
  }
  min?: string
  max?: string
  sortOrder?: number
  skip?: boolean
  footerSection: boolean
  formId?: string
  required?: boolean
}

interface Props {
  input: PreferenceInputType
  field: Partial<
    ControllerRenderProps<
      {
        preferences?: {
          value?: string
          key?: string
        }[]
      },
      `preferences.${number}`
    >
  >
}

type InterestValue = {
  key: string
  value: boolean
}

const setDefaultInterestValues = (
  interestValues: Array<InterestValue>,
): InterestValue[] => {
  const defaultValues: Array<InterestValue> = [
    { key: 'seating', value: false },
    { key: 'lighting', value: false },
    { key: 'tables', value: false },
    { key: 'mirrors', value: false },
    { key: 'outdoorPatio', value: false },
    { key: 'rugsObjects', value: false },
    { key: 'storiesInspiration', value: false },
    { key: 'events', value: false },
  ]

  // override if not in interests array
  return defaultValues.map((defaultValue) => {
    const userValue = interestValues.find((i) => i.key === defaultValue.key)
    if (userValue) {
      return userValue
    }
    return defaultValue
  })
}

const PrefrenceInputRender: FC<Props> = ({ input, field }) => {
  const fieldValue = field.value?.value ?? ''
  const fieldKey = field.value?.key ?? ''
  const { isOpen, open, close } = useDisclosure()

  if (input.inputType == 'interests') {
    const interestValues: Array<InterestValue> = setDefaultInterestValues(
      JSON.parse(fieldValue),
    )

    return (
      <div className={clsx(s.customInput, s.formInputWrapper)}>
        <div className={s.customInput__label}>Interests</div>
        <div className={s.customInput__input}>
          <a onClick={open}>Edit</a>
        </div>

        <ModalOverlay
          isDismissable
          isOpen={isOpen}
          onOpenChange={(isOpen) => (isOpen ? open() : close())}
          className={s.overlay}
        >
          <Modal className={s.modal}>
            <Dialog>
              <div className={s.close}>
                <button className={s.closeButton} type="button" onClick={close}>
                  <Close className="h-6 w-6" />
                </button>
              </div>
              <div className={nms.root}>
                <div className={nms.headline}>INTERESTS</div>
                <div className={nms.section}>
                  <div className={s.interestsWrapper}>
                    {interestValues.map((interest) => (
                      <FormControl
                        key={interest.key}
                        className={s.formInputWrapper}
                        layout="horizontal"
                      >
                        <Checkbox
                          value={interest.key}
                          checked={interest.value}
                          onChange={() => {
                            const newInterestValues = interestValues.map(
                              (i) => {
                                if (i.key === interest.key) {
                                  return { key: i.key, value: !i.value }
                                }
                                return i
                              },
                            )

                            field.onChange({
                              value: JSON.stringify(newInterestValues),
                              key: field.value.key,
                            })
                          }}
                        />
                        <FormControl.Caption>
                          {interest.key
                            .replace(/([a-z])([A-Z])/g, '$1 & $2')
                            .replace(/^./, (str) => str.toUpperCase())}
                        </FormControl.Caption>
                      </FormControl>
                    ))}
                  </div>
                </div>
                <div className={nms.section}>
                  <Button
                    form={input.formId}
                    type="submit"
                    className={s.button}
                    size="small"
                    variant="secondary"
                    onClick={() => {
                      requestAnimationFrame(() => {
                        close()
                      })
                    }}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </Dialog>
          </Modal>
        </ModalOverlay>
      </div>
    )
  }

  return (
    <FormControl
      required={input.required}
      className={s.formInputWrapper}
      layout="horizontal"
    >
      <FormControl.Label>{input.inputName}</FormControl.Label>

      {input.inputType === 'text' && (
        <TextInput
          onChange={(e) => {
            field.onChange({
              value: e.target.value,
              key: field.value.key,
            })
          }}
          value={field.value.value}
          className={s.input}
        />
      )}

      {input.inputType === 'date' && (
        <TextInput
          onChange={(e) => {
            field.onChange({
              value: e.target.value,
              key: field.value.key,
            })
          }}
          type="date"
          min={input.min}
          max={input.max}
          value={field.value.value}
          className={s.input}
        />
      )}

      {input.inputType === 'checkbox' && (
        <Checkbox
          onChange={(e) => {
            field.onChange({
              value: fieldValue == 'true' ? 'false' : 'true',
              key: field.value.key,
            })
          }}
          value={fieldKey}
          checked={fieldValue == 'true'}
        />
      )}
      {input.inputType === 'checkbox' && input.checkboxText && (
        <FormControl.Caption>
          {fieldValue?.toLowerCase() == 'true'
            ? input.checkboxText.true
            : input.checkboxText.false}
        </FormControl.Caption>
      )}
    </FormControl>
  )
}

PrefrenceInputRender.displayName = 'PrefrenceInputRender'
export default PrefrenceInputRender
